import {
  School,
  SportsEsports,
  EmojiEvents,
  Lightbulb,
} from "@mui/icons-material";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
} from "@mui/material";
import i18next from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";

export function GameInfoView(props: { title?: string }) {
  const [nsLoaded, setNsLoaded] = React.useState(false);

  React.useEffect(() => {
    i18next.loadNamespaces("gameInfo", (err, t) => {
      if (!err) {
        setNsLoaded(true);
      } else {
        console.log("ERROR LOADING NS", err);
      }
    });
  }, []);

  const { t } = useTranslation();
  if (!nsLoaded) return <></>;

  const { title = t("gameInfo:title") } = props;

  const Section = ({
    title,
    icon,
    children,
  }: {
    title: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
  }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 2,
        borderRadius: 3,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        {icon && <Box sx={{ mr: 1.5, color: "primary.main" }}>{icon}</Box>}
        <Typography variant="h6" fontWeight={600} color="primary">
          {title}
        </Typography>
      </Box>
      {children}
    </Paper>
  );

  return (
    <Box
      marginX={"auto"}
      maxWidth={"700px"}
      p={2}
      sx={{
        "& .MuiTypography-body1": {
          lineHeight: 1.7,
        },
      }}
    >
      {title !== "" && (
        <Typography
          textAlign={"center"}
          variant="h4"
          fontWeight={600}
          gutterBottom
          sx={{ mb: 4 }}
        >
          {title}
        </Typography>
      )}

      {/* Intro */}
      <Section title={t("gameInfo:intro.title")} icon={<SportsEsports />}>
        <Typography variant="body1">{t("gameInfo:intro.text")}</Typography>
      </Section>

      {/* Pieces */}
      <Section title={t("gameInfo:pieces.title")}>
        <Typography variant="body1" gutterBottom>
          {t("gameInfo:pieces.text1")}
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("gameInfo:pieces.feature1")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:pieces.feature2")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:pieces.feature3")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:pieces.feature4")} />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("gameInfo:pieces.text2")}
        </Typography>
      </Section>

      {/* Setup */}
      <Section title={t("gameInfo:setup.title")}>
        <Typography variant="body1" gutterBottom>
          {t("gameInfo:setup.text1")}
        </Typography>
        <Typography variant="body1">{t("gameInfo:setup.text2")}</Typography>
      </Section>

      {/* Gameplay */}
      <Section title={t("gameInfo:gameplay.title")} icon={<SportsEsports />}>
        <List dense>
          <ListItem>
            <ListItemText primary={t("gameInfo:gameplay.step1")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:gameplay.step2")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:gameplay.step3")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:gameplay.step4")} />
          </ListItem>
        </List>
        <Typography variant="body1" sx={{ mt: 1 }}>
          {t("gameInfo:gameplay.text1")}
        </Typography>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "warning.main",
          }}
        >
          <Typography variant="body2" fontWeight={600}>
            {t("gameInfo:gameplay.important")}
          </Typography>
        </Box>
      </Section>

      {/* Winning */}
      <Section title={t("gameInfo:winning.title")} icon={<EmojiEvents />}>
        <Typography variant="body1" gutterBottom>
          {t("gameInfo:winning.text1")}
        </Typography>
        <Typography variant="body1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
          {t("gameInfo:winning.text2")}
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("gameInfo:winning.row1")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:winning.row2")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:winning.row3")} />
          </ListItem>
        </List>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "success.main",
          }}
        >
          <Typography variant="body2">
            {t("gameInfo:winning.example")}
          </Typography>
        </Box>
        <Typography variant="body1" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
          {t("gameInfo:winning.text3")}
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary={t("gameInfo:winning.trait1")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:winning.trait2")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:winning.trait3")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:winning.trait4")} />
          </ListItem>
        </List>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {t("gameInfo:winning.draw")}
        </Typography>
      </Section>

      {/* Expert Mode */}
      <Section title={t("gameInfo:expert.title")} icon={<School />}>
        <Chip
          label="Advanced"
          color="error"
          size="small"
          sx={{ mb: 2, fontWeight: 600 }}
        />
        <Typography variant="body1" gutterBottom>
          {t("gameInfo:expert.text1")}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {t("gameInfo:expert.text2")}
        </Typography>
        <Typography variant="body1" fontWeight={600} gutterBottom>
          {t("gameInfo:expert.text3")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("gameInfo:expert.grid")}
        </Typography>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "action.hover",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "error.main",
          }}
        >
          <Typography variant="body2">
            {t("gameInfo:expert.difficulty")}
          </Typography>
        </Box>
      </Section>

      {/* Strategy */}
      <Section title={t("gameInfo:strategy.title")} icon={<Lightbulb />}>
        <List dense>
          <ListItem>
            <ListItemText primary={t("gameInfo:strategy.tip1")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:strategy.tip2")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:strategy.tip3")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:strategy.tip4")} />
          </ListItem>
          <ListItem>
            <ListItemText primary={t("gameInfo:strategy.tip5")} />
          </ListItem>
        </List>
      </Section>

      {/* Share */}
      <Section title={t("gameInfo:share.title")}>
        <Typography variant="body1" gutterBottom>
          {t("gameInfo:share.text1")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("gameInfo:share.text2")}
        </Typography>
      </Section>
    </Box>
  );
}
